import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { isAuthRequired } from '@/lib/environment';

/**
 * Returns true once a real session has been restored.
 * In auth-required mode we keep this "sticky" after first success and only
 * reset on explicit sign-out, so transient auth refresh gaps never blank data.
 */
let globalSessionReady = !isAuthRequired();
let authBootstrapStarted = false;
const listeners = new Set<(ready: boolean) => void>();

const publishSessionReady = (ready: boolean) => {
  if (ready === globalSessionReady) return;
  globalSessionReady = ready;
  listeners.forEach((fn) => fn(ready));
};

if (!authBootstrapStarted && isAuthRequired()) {
  authBootstrapStarted = true;

  supabase.auth.getSession().then(({ data: { session } }) => {
    if (session) {
      publishSessionReady(true);
    }
  });

  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT') {
      publishSessionReady(false);
      return;
    }

    if (session) {
      publishSessionReady(true);
    }
  });
}

export function useSessionReady(): boolean {
  const [ready, setReady] = useState(globalSessionReady);

  useEffect(() => {
    if (globalSessionReady !== ready) setReady(globalSessionReady);

    listeners.add(setReady);
    return () => {
      listeners.delete(setReady);
    };
  }, [ready]);

  return ready;
}
